import axios from "axios";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CartaoNoAcompanhamento } from "@/features/cartao-fidelidade";
import { isPushSupported, subscribeToOrderPush } from "@/utils/notifications";

import Atalhos from "./Atalhos";
import CartaoStatus from "./CartaoStatus";
import { FINALIZADOS } from "./etapas";
import ItensDoPedido, { imagem } from "./ItensDoPedido";
import RodapeWhatsApp from "./RodapeWhatsApp";

const API = "/api";

// Mesmo tom do cardápio (fundo zinc-900, cartões zinc-800, borda zinc-700):
// antes o escuro daqui era um degrau mais preto e destoava (dono, 24/10/2026).
const tema = (escuro) => ({
  escuro,
  fundo: escuro ? "bg-zinc-900" : "bg-gray-50",
  card: escuro ? "bg-zinc-800" : "bg-white",
  borda: escuro ? "border-zinc-700" : "border-gray-200",
  divisoria: escuro ? "divide-zinc-700" : "divide-gray-100",
  chip: escuro ? "bg-zinc-700" : "bg-gray-100",
  texto: escuro ? "text-white" : "text-gray-900",
  mudo: escuro ? "text-zinc-400" : "text-gray-500",
  fundoResumo: escuro ? "bg-zinc-900/40" : "bg-gray-50/70",
});

/**
 * Acompanhamento do pedido no cardápio digital (repaginado em 23/10/2026, pedido
 * do dono): sem o "hero" grande; o status é o primeiro card (código, valor,
 * retirada/entrega, etapa e bolinhas com horário). Embaixo: atalhos (mapa ou
 * rastreio, joguinho), o cartão fidelidade e os itens. O WhatsApp fica num
 * botão fixo no rodapé (24/10/2026).
 * A rota pública `/public/pedidos/{id}` é consultada a cada 5 s.
 */
export default function AcompanhamentoPedido({ pedido: inicial, onClose, darkMode, companyPhone, companySettings,
  allProducts = [], produtos = [], tempoMedioMin = null }) {
  const [pedido, setPedido] = useState(inicial);
  const [cancelarAberto, setCancelarAberto] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);
  const t = tema(darkMode);

  useEffect(() => {
    if (!inicial?.id) return undefined;
    const buscar = () => axios.get(`${API}/public/pedidos/${inicial.id}`).then((r) => r.data && setPedido(r.data)).catch(() => {});
    buscar();
    const i = setInterval(buscar, 5000);
    return () => clearInterval(i);
  }, [inicial?.id]);

  // Notificação do navegador: com a permissão já dada, inscreve sozinho neste pedido.
  useEffect(() => {
    if (!inicial?.id || !isPushSupported()) return;
    if (Notification.permission === "granted") subscribeToOrderPush(inicial.id).catch(() => {});
  }, [inicial?.id]);

  const retirada = pedido?.tipo_entrega === "pickup";
  const cancelado = pedido?.status === "cancelado";
  const emAndamento = !cancelado && !FINALIZADOS.includes(pedido?.status);
  const nomeLoja = companySettings?.fantasy_name || companySettings?.company_name || "Restaurante";
  const endereco = companySettings?.address || "";

  // Previsão: hora do pedido + tempo médio da loja (o mesmo do cabeçalho do cardápio).
  let previsao = null;
  if (tempoMedioMin && pedido?.created_at && ["aceito", "producao"].includes(pedido?.status)) {
    const d = new Date(new Date(pedido.created_at).getTime() + Number(tempoMedioMin) * 60000);
    previsao = d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  }

  const fone = (companyPhone || "").replace(/\D/g, "");
  const codigo = (pedido?.codigo || "").replace(/^#/, "");
  const linkWhatsApp = fone
    ? `https://wa.me/${fone.startsWith("55") ? fone : `55${fone}`}?text=${encodeURIComponent(`Gostaria de acompanhar o pedido #${codigo}`)}`
    : null;
  const abrirMapa = () => {
    const alvo = endereco || nomeLoja;
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(alvo)}`, "_blank");
  };

  const pedirCancelamento = async () => {
    if (!pedido?.id) return;
    setEnviando(true);
    try {
      const r = await axios.post(`${API}/public/pedidos/${pedido.id}/solicitar-cancelamento`, { motivo: motivo.trim() });
      setPedido(r.data);
      toast.success("Pedido de cancelamento enviado! Aguarde a decisão do restaurante.");
      setCancelarAberto(false);
      setMotivo("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao pedir cancelamento");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div data-clarity-mask="true" className={`fixed inset-0 z-[100] overflow-y-auto ${t.fundo}`} data-e2e="acompanhamento-pedido">
      {/* Cabeçalho na cor de personalização da loja (--primary, a mesma do topo
          do cardápio), nos dois temas (dono, 24/10/2026) */}
      <header className="sticky top-0 z-10 bg-primary text-white shadow-md">
        <div className="max-w-xl mx-auto px-3 py-2.5 flex items-center gap-3">
          <button type="button" onClick={onClose} aria-label="Fechar"
            className="w-9 h-9 grid place-items-center rounded-full shrink-0 hover:bg-white/15 text-white">
            <X className="w-5 h-5" />
          </button>
          {companySettings?.logo_url && (
            <img src={imagem(companySettings.logo_url)} alt="" className="w-8 h-8 rounded-full object-cover bg-white ring-2 ring-white/40 shrink-0"
              onError={(e) => { e.currentTarget.style.display = "none"; }} />
          )}
          <div className="min-w-0">
            <p className="text-[11px] leading-none text-white/80">Acompanhe seu pedido</p>
            <p className="text-sm font-bold truncate text-white">{nomeLoja}</p>
          </div>
        </div>
      </header>

      <main className={`max-w-xl mx-auto p-4 space-y-3.5 ${linkWhatsApp ? "pb-28" : "pb-10"}`}>
        <CartaoStatus pedido={pedido} retirada={retirada} previsao={previsao} loja={{ nome: nomeLoja, endereco }} t={t}
          onCancelar={() => setCancelarAberto(true)} />
        <Atalhos pedido={pedido} retirada={retirada} emAndamento={emAndamento} abrirMapa={abrirMapa} />
        {!cancelado && <CartaoNoAcompanhamento pedidoId={pedido?.id} darkMode={darkMode} className={`rounded-3xl border ${t.card} ${t.borda}`} />}
        <ItensDoPedido pedido={pedido} retirada={retirada} produtos={produtos} todosProdutos={allProducts} t={t} />
      </main>
      <RodapeWhatsApp link={linkWhatsApp} emAndamento={emAndamento} escuro={darkMode} />

      <Dialog open={cancelarAberto} onOpenChange={setCancelarAberto}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Solicitar cancelamento</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              O restaurante ainda precisa aprovar. Você recebe uma mensagem no WhatsApp assim que ele decidir.
              Se quiser explicar o motivo, escreva abaixo (opcional).
            </p>
            <textarea value={motivo} onChange={(e) => setMotivo(e.target.value.slice(0, 500))} autoFocus
              placeholder="Ex.: mudei de ideia, imprevisto no horário..."
              className="w-full min-h-[80px] rounded-lg border p-2 text-sm resize-none bg-background" />
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setCancelarAberto(false)} disabled={enviando}
                className="h-9 px-3 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted">Voltar</button>
              <button type="button" onClick={pedirCancelamento} disabled={enviando}
                className="h-9 px-4 rounded-lg text-sm font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-60">
                Enviar pedido
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
